import { Injectable, signal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EndSessionPopupRequest,
  EndSessionRequest,
  EventMessage,
  EventType,
  IPublicClientApplication,
  InteractionRequiredAuthError,
  PublicClientApplication,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { Observable, firstValueFrom, from } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private msalClientReady: Promise<void> | null = null;
  private readonly inactivityLogoutInProgress = signal(false);

  constructor(
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService
  ) {
    this.registerLoginSuccessHook();
    this.initializationPromise = this.initialize();
  }

  get instance(): IPublicClientApplication {
    return this.msalService.instance;
  }

  get msalSubject$() {
    return this.msalBroadcastService.msalSubject$;
  }

  get inProgress$() {
    return this.msalBroadcastService.inProgress$;
  }

  handleRedirectObservable(): Observable<AuthenticationResult | null> {
    return this.msalService.handleRedirectObservable();
  }

  async ready(): Promise<void> {
    if (!this.initializationPromise) {
      this.initializationPromise = this.initialize();
    }

    return this.initializationPromise;
  }

  loginRedirect(request?: RedirectRequest): Observable<void> {
    return from(this.ensureClientInitialized()).pipe(
      switchMap(() => this.msalService.loginRedirect(request))
    );
  }

  loginPopup(request?: PopupRequest): Observable<AuthenticationResult> {
    return from(this.ensureClientInitialized()).pipe(
      switchMap(() => this.msalService.loginPopup(request))
    );
  }

  async login(options?: {
    usePopup?: boolean;
    request?: PopupRequest | RedirectRequest;
  }): Promise<void> {
    await this.ensureClientInitialized();

    if (options?.usePopup) {
      const result = await this.msalService.instance.loginPopup(
        options.request as PopupRequest | undefined
      );
      if (result?.account) {
        this.msalService.instance.setActiveAccount(result.account);
        this.handlePostLogin('LOGIN_POPUP');
      }
      return;
    }

    await this.msalService.loginRedirect(options?.request as RedirectRequest | undefined);
  }

  logoutRedirect(request?: EndSessionRequest): Observable<void> {
    return this.msalService.logoutRedirect(request);
  }

  logoutPopup(request?: EndSessionPopupRequest): Observable<void> {
    return this.msalService.logoutPopup(request);
  }

  async logout(options?: {
    usePopup?: boolean;
    request?: EndSessionPopupRequest | EndSessionRequest;
  }): Promise<void> {
    await this.ensureClientInitialized();

    if (options?.usePopup) {
      await this.msalService.logoutPopup(options.request as EndSessionPopupRequest | undefined);
      return;
    }

    const request = this.buildLogoutRequest(options?.request as EndSessionRequest | undefined);
    await this.msalService.logoutRedirect(request);
  }

  ensureActiveAccount(): void {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
        activeAccount = accounts[0];
      }
    }

    if (activeAccount) {
      // Hook for custom logic to run once account becomes active
      this.logPostLogin('ENSURE_ACTIVE_ACCOUNT', activeAccount.username);
    }
  }

  async acquireToken(scopes: string[]): Promise<string | null> {
    await this.ready();
    this.ensureActiveAccount();
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return null;
    }

    try {
      const result = await firstValueFrom(this.msalService.acquireTokenSilent({ account, scopes }));
      return result?.accessToken ?? null;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('Silent token acquisition requires interaction', error);
        return null;
      }

      console.error('acquireTokenSilent failed', error);
      return null;
    }
  }

  hasAuthenticatedAccount(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  async getCurrentAccount(): Promise<AccountInfo | null> {
    await this.ready();

    if (!this.hasAuthenticatedAccount()) {
      return null;
    }

    this.ensureActiveAccount();
    return this.msalService.instance.getActiveAccount();
  }

  handlePostLogin(source?: string): void {
    this.ensureActiveAccount();

    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      this.logPostLogin(source ?? 'POST_LOGIN', activeAccount.username);
    }
  }

  private registerLoginSuccessHook(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(filter((message: EventMessage) => message.eventType === EventType.LOGIN_SUCCESS))
      .subscribe(() => this.handlePostLogin('LOGIN_SUCCESS_EVENT'));
  }

  private async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.ensureClientInitialized();
    this.ensureActiveAccount();
    this.initialized = true;
  }

  private async ensureClientInitialized(): Promise<void> {
    if (!this.msalClientReady) {
      const instance = this.msalService.instance as IPublicClientApplication & {
        _isInitialized?: boolean;
      };
      this.msalClientReady = (async () => {
        if (!instance._isInitialized) {
          await instance.initialize();
        }
      })();
    }

    await this.msalClientReady;
  }

  private logPostLogin(source: string, username: string): void {
    // Central spot for custom post-login logic without affecting current flows
    console.debug('[AuthService] Post-login hook triggered', { source, username });
  }

  hasValidAccessToken(): boolean {
    if (!this.initialized) {
      return true;
    }

    this.ensureActiveAccount();
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return false;
    }

    const expiry = this.getCachedAccessTokenExpiry(account.homeAccountId);
    if (expiry === null) {
      const expClaim = (account.idTokenClaims as { exp?: number | string } | undefined)?.exp;
      const expiryFromIdToken = this.normalizeEpoch(expClaim);
      return expiryFromIdToken !== null && expiryFromIdToken > Date.now();
    }

    return expiry > Date.now();
  }

  async logoutDueToInactivity(): Promise<void> {
    if (this.inactivityLogoutInProgress()) {
      return;
    }

    this.inactivityLogoutInProgress.set(true);
    try {
      if (!(await this.isMsalTokenExpired())) {
        return;
      }

      console.info('[AuthService] Inactivity logout initiated');
      await this.logout();
    } finally {
      this.inactivityLogoutInProgress.set(false);
    }
  }

  private buildLogoutRequest(request?: EndSessionRequest): EndSessionRequest | undefined {
    const postLogoutRedirectUri = request?.postLogoutRedirectUri ?? this.getLogoutRedirectTarget();
    const account = request?.account ?? this.msalService.instance.getActiveAccount() ?? undefined;

    if (!postLogoutRedirectUri && !account && !request) {
      return undefined;
    }

    return {
      ...request,
      account,
      postLogoutRedirectUri,
    };
  }

  private getLogoutRedirectTarget(): string | undefined {
    const configuredTarget = window.location.origin;

    if (configuredTarget) {
      return configuredTarget;
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }

    return undefined;
  }

  private async isMsalTokenExpired(): Promise<boolean> {
    await this.ready();
    this.ensureActiveAccount();
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      return true;
    }

    const accessTokenExpiry = this.getCachedAccessTokenExpiry(activeAccount.homeAccountId);
    if (accessTokenExpiry !== null) {
      return accessTokenExpiry <= Date.now();
    }

    const expClaim = (activeAccount.idTokenClaims as { exp?: number | string } | undefined)?.exp;
    if (expClaim === undefined) {
      return false;
    }

    const expiryMs = this.normalizeEpoch(expClaim);
    return expiryMs !== null ? expiryMs <= Date.now() : false;
  }

  private getCachedAccessTokenExpiry(homeAccountId: string): number | null {
    const instance = this.msalService.instance as PublicClientApplication;
    if (typeof instance.getTokenCache !== 'function') {
      return null;
    }

    const tokenCache = instance.getTokenCache() as unknown as {
      serialize?: () => string;
    };
    if (typeof tokenCache.serialize !== 'function') {
      return null;
    }
    try {
      const serialized = tokenCache.serialize();
      if (!serialized) {
        return null;
      }

      const parsed = JSON.parse(serialized) as {
        AccessToken?: Record<
          string,
          {
            homeAccountId?: string;
            expiresOn?: string | number;
            extendedExpiresOn?: string | number;
          }
        >;
      };

      if (!parsed.AccessToken) {
        return null;
      }

      const matchingToken = Object.values(parsed.AccessToken).find(
        (token) => token.homeAccountId === homeAccountId
      );

      if (!matchingToken) {
        return null;
      }

      const rawExpiry = matchingToken.expiresOn ?? matchingToken.extendedExpiresOn;
      return this.normalizeEpoch(rawExpiry ?? null);
    } catch (error) {
      console.warn('[AuthService] Unable to read token cache for inactivity check', error);
      return null;
    }
  }

  private normalizeEpoch(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    const numeric = typeof value === 'string' ? Number(value) : (value as number);

    if (!Number.isFinite(numeric)) {
      return null;
    }

    // Values from MSAL cache are in seconds; convert to ms when appropriate.
    return numeric > 1e12 ? numeric : numeric * 1000;
  }
}
